/* Only controls videos rendered by cb-visible-video; product galleries stay independent. */
(() => {
  if (window.cbVisibleVideoInitialized) return;
  window.cbVisibleVideoInitialized = true;

  const states = new Map();
  const mobile = window.matchMedia('(max-width: 767px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isDisplayed = (video) => video.getClientRects().length > 0 && video.getBoundingClientRect().width > 0;
  const automatic = (state) => state.autoplay && !reducedMotion.matches && !state.userPaused && !state.playBlocked;

  function pause(state) {
    state.shouldPlay = false;
    state.video.pause();
  }

  function load(state) {
    const video = state.video;
    if (state.loaded || document.hidden || !isDisplayed(video)) return;
    const sources = [...video.querySelectorAll('source[data-src]')];
    // Native HLS is useful for non-looping video; MP4 is portable for loops.
    const hls = !video.loop && video.canPlayType('application/vnd.apple.mpegurl')
      ? sources.find((source) => /mpegurl/i.test(source.type)) : null;
    const mp4 = sources.filter((source) => source.type === 'video/mp4')
      .sort((a, b) => Number(a.dataset.height) - Number(b.dataset.height));
    const target = mobile.matches ? 720 : 1080;
    const selected = hls || mp4.find((source) => Number(source.dataset.height) >= target)
      || mp4[mp4.length - 1] || sources.find((source) => video.canPlayType(source.type));
    if (!selected) return;
    video.preload = 'metadata';
    video.src = selected.dataset.src;
    state.loaded = true;
  }

  function sync(state) {
    const video = state.video;
    const displayed = isDisplayed(video);
    if (!displayed) {
      pause(state);
      // Cancel an old responsive variant, including buffering, on breakpoint change.
      if (state.loaded) {
        video.removeAttribute('src');
        video.load();
        state.loaded = false;
      }
      video.removeAttribute('poster');
      return;
    }
    video.controls = state.controls || !state.autoplay || reducedMotion.matches || state.playBlocked;
    if (document.hidden) { pause(state); return; }
    if (state.near) {
      const poster = mobile.matches ? video.dataset.cbPosterMobile : video.dataset.cbPosterDesktop;
      if (poster && video.getAttribute('poster') !== poster) video.poster = poster;
      load(state);
    }
    if (!state.visible) { pause(state); state.manual = false; return; }
    if (state.loaded && (automatic(state) || state.manual)) {
      if (!video.paused || state.shouldPlay) return;
      state.shouldPlay = true;
      video.preload = 'auto';
      const play = video.play();
      if (play) play.catch((error) => {
        state.shouldPlay = false;
        // A visibility change can legitimately interrupt a pending play().
        if (error.name !== 'AbortError') {
          state.playBlocked = true;
          video.controls = true;
        }
      });
    } else if (!state.manual) pause(state);
  }

  const nearObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const state = states.get(entry.target);
      if (!state) return;
      state.near = entry.isIntersecting;
      sync(state);
    });
  }, { rootMargin: '250px 0px', threshold: 0 }) : null;

  const visibleObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const state = states.get(entry.target);
      if (!state) return;
      state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.01;
      if (state.visible) state.near = true;
      sync(state);
    });
  }, { threshold: [0, 0.01] }) : null;

  function scan(root = document) {
    root.querySelectorAll('video[data-cb-video]').forEach((video) => {
      if (states.has(video)) return;
      const state = { video, autoplay: video.dataset.cbAutoplay === 'true', controls: video.controls,
        near: !nearObserver, visible: !visibleObserver, loaded: false, shouldPlay: false,
        userPaused: false, manual: false, playBlocked: false };
      states.set(video, state);
      video.muted = state.autoplay;
      video.addEventListener('pause', () => {
        if (state.shouldPlay && state.visible && !document.hidden && isDisplayed(video)) {
          state.userPaused = true;
          state.manual = false;
          state.shouldPlay = false;
        }
      });
      video.addEventListener('play', () => {
        state.userPaused = false;
        if (!state.shouldPlay) state.manual = true;
        state.shouldPlay = true;
      });
      nearObserver?.observe(video);
      visibleObserver?.observe(video);
      sync(state);
    });
  }

  function syncAll() { states.forEach(sync); }
  document.addEventListener('visibilitychange', syncAll);
  mobile.addEventListener('change', syncAll);
  reducedMotion.addEventListener('change', syncAll);
  // Re-evaluate CSS visibility even when resize remains on the same side of a breakpoint.
  let resizeFrame;
  window.addEventListener('resize', () => {
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(syncAll);
  }, { passive: true });
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    states.forEach((state, video) => {
      if (!event.target.contains(video)) return;
      pause(state);
      nearObserver?.unobserve(video);
      visibleObserver?.unobserve(video);
      states.delete(video);
    });
  });
  scan();
})();
